import {
	ATTRIBUTES,
	isVariantChangeEvent,
	openWaitlistModal,
	PDP_REVIEWS_READY_EVENT,
	publish,
	PUB_SUB_EVENTS,
	SELECTORS,
	subscribe,
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type ModalDialog } from '@/scripts/theme/modal-dialog'
import {
	addItemsToCart,
	getDOMCartSectionApiIds, hasDomCart,
	renderResponseToCartDrawer,
	showCartErrorNotification
} from '@/scripts/core/cart-functions';
import { CartErrorResponse } from '@/scripts/shopify'

export class ProductForm extends UcoastEl {
	static htmlSelector = 'product-form'
	static selectors = {
		form: 'form',
		formIdEl: '[name=id]',
		submitButton: '[type="submit"]',
		submitButtonMessage: '[type="submit"] span',
		soldOutMessage: '[data-uc-sold-out-message]',
		errorMessageWrapper: '[data-uc-product-form-error-message-wrapper]',
		errorMessage: '[data-uc-product-form-error-message]',
	}

	form: HTMLFormElement
	formIdEl: HTMLInputElement
	submitButton: HTMLButtonElement
	errorMessageWrapper?: HTMLElement
	errorMessage?: HTMLElement
	hideErrors: boolean
	error?: boolean
	stickyObserver?: IntersectionObserver
	stickyBoundaryObserver?: IntersectionObserver
	stickyBoundaryScrollHandler?: EventListener
	stickyBoundaryFrame?: number
	variantChangeUnsubscriber?: () => void

	constructor() {
		super()

		this.form = q.rs(ProductForm.selectors.form, this)
		this.formIdEl = q.rs(ProductForm.selectors.formIdEl, this.form)
		this.formIdEl.disabled = false
		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))

		this.submitButton = q.rs(ProductForm.selectors.submitButton, this)
		if (hasDomCart()) {
			this.submitButton.setAttribute('aria-haspopup', 'dialog')
		}

		this.hideErrors = this.dataset.hideErrors === 'true'
	}

	override connectedCallback() {
		this.setupStickyATC()
		document.addEventListener(
			PDP_REVIEWS_READY_EVENT,
			this.setupStickyATC
		)
	}

	override disconnectedCallback() {
		document.removeEventListener(
			PDP_REVIEWS_READY_EVENT,
			this.setupStickyATC
		)
		this.stickyObserver?.disconnect()
		this.stickyBoundaryObserver?.disconnect()
		if (this.stickyBoundaryScrollHandler) {
			window.removeEventListener(
				'scroll',
				this.stickyBoundaryScrollHandler
			)
		}
		if (this.stickyBoundaryFrame !== undefined) {
			window.cancelAnimationFrame(this.stickyBoundaryFrame)
			this.stickyBoundaryFrame = undefined
		}
		this.variantChangeUnsubscriber?.()
	}

	setupStickyATC = () => {
		const hasSticky = this.closest('sticky-atc')
		const productInfo = this.closest<HTMLElement>('product-info')
		if (hasSticky || !productInfo || typeof IntersectionObserver === 'undefined') return

		const section = this.closest('section')
		const stickyATC = section?.querySelector<HTMLElement>('sticky-atc')
		if (!stickyATC) return
		this.stickyObserver?.disconnect()
		this.stickyBoundaryObserver?.disconnect()
		if (this.stickyBoundaryScrollHandler) {
			window.removeEventListener(
				'scroll',
				this.stickyBoundaryScrollHandler
			)
		}
		if (this.stickyBoundaryFrame !== undefined) {
			window.cancelAnimationFrame(this.stickyBoundaryFrame)
			this.stickyBoundaryFrame = undefined
		}
		this.variantChangeUnsubscriber?.()
		const reviewsBoundary = document
			.querySelector<HTMLElement>('#judgeme_product_reviews')
			?.closest<HTMLElement>('.shopify-section')
		const stickyBoundary = reviewsBoundary?.hasAttribute(
			'data-pdp-reviews-ready'
		)
			? reviewsBoundary
			: document.querySelector<HTMLElement>('footer.footer')
		let submitButtonVisible = true
		let boundaryVisible = false
		const updateStickyVisibility = () => {
			stickyATC.classList.toggle(
				'active',
				!submitButtonVisible && !boundaryVisible
			)
			stickyATC
				.querySelector<ProductForm>('product-form')
				?.syncStickyFormFields()
		}

		this.stickyObserver = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				submitButtonVisible = entry.isIntersecting
				updateStickyVisibility()
			},
			{
				threshold: 0,
			}
		)
		this.stickyObserver.observe(this.submitButton)
		if (stickyBoundary) {
			const updateBoundaryFromScroll = () => {
				if (this.stickyBoundaryFrame !== undefined) return
				this.stickyBoundaryFrame = window.requestAnimationFrame(() => {
					this.stickyBoundaryFrame = undefined
					boundaryVisible =
						stickyBoundary.getBoundingClientRect().top <
						window.innerHeight
					updateStickyVisibility()
				})
			}
			this.stickyBoundaryScrollHandler = updateBoundaryFromScroll
			window.addEventListener('scroll', updateBoundaryFromScroll, {
				passive: true,
			})
			this.stickyBoundaryObserver = new IntersectionObserver(
				(entries) => {
					const entry = entries[0]
					boundaryVisible =
						entry.isIntersecting || entry.boundingClientRect.top < 0
					updateStickyVisibility()
				},
				{ threshold: 0 }
			)
			this.stickyBoundaryObserver.observe(stickyBoundary)
			updateBoundaryFromScroll()
		}
		this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
			if (!isVariantChangeEvent(event)) return
			if (event.data.sectionId !== q.ra(productInfo, 'data-section')) return
			window.requestAnimationFrame(() => {
				const stickyProductForm = stickyATC.querySelector<ProductForm>('product-form')
				stickyProductForm?.syncStickyFormFields()
			})
		})
	}

	syncStickyFormFields() {
		const stickyATC = this.closest('sticky-atc')
		if (!stickyATC || !this.form.id.endsWith('-sticky')) return

		const mainFormId = this.form.id.replace(/-sticky$/, '')
		const mainForm = document.getElementById(mainFormId)
		if (!(mainForm instanceof HTMLFormElement)) return

		const mainVariantInput = mainForm.querySelector<HTMLInputElement>('[name="id"]')
		if (mainVariantInput) {
			this.formIdEl.value = mainVariantInput.value
		}

		const mainSellingPlan = mainForm.querySelector<HTMLInputElement>('[name="selling_plan"]')
		const stickySellingPlan = this.form.querySelector<HTMLInputElement>('[name="selling_plan"]')
		if (mainSellingPlan && stickySellingPlan) {
			stickySellingPlan.value = mainSellingPlan.value
		}

		const mainSubmitButton = mainForm.querySelector<HTMLButtonElement>('[name="add"]')
		if (!mainSubmitButton) return
		const shadePicker = mainForm.closest(
			'hero-featured-product[data-requires-shade]'
		)
		const requiresShadeSelection =
			shadePicker && !shadePicker.hasAttribute('data-has-selected-shade')

		const sourceOosTrigger = q.oa(mainSubmitButton, 'data-oos-popup-trigger')
		if (sourceOosTrigger) {
			this.submitButton.setAttribute('data-oos-popup-trigger', sourceOosTrigger)
		} else {
			this.submitButton.removeAttribute('data-oos-popup-trigger')
		}

		const sourceProductTitle = q.oa(mainSubmitButton, 'data-product-title')
		if (sourceProductTitle) {
			this.submitButton.setAttribute('data-product-title', sourceProductTitle)
		} else {
			this.submitButton.removeAttribute('data-product-title')
		}

		if (requiresShadeSelection || mainSubmitButton.hasAttribute('disabled')) {
			this.submitButton.setAttribute('disabled', 'disabled')
			this.submitButton.setAttribute('aria-disabled', 'true')
		} else {
			this.submitButton.removeAttribute('disabled')
			this.submitButton.removeAttribute('aria-disabled')
		}

		const sourceButtonText = mainSubmitButton.querySelector('span')
		const stickyButtonText = this.submitButton.querySelector('span')
		if (sourceButtonText && stickyButtonText) {
			stickyButtonText.textContent = sourceButtonText.textContent
		}
	}

	getSpinner() {
		return q.rs(SELECTORS.loadingOverlaySpinner, this)
	}

	onSubmitHandler(event: Event) {
		event.preventDefault()
		const shadePicker = this.closest('hero-featured-product[data-requires-shade]')
		if (shadePicker && !shadePicker.hasAttribute('data-has-selected-shade')) return
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return
		this.syncStickyFormFields()
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return
		const oosId = q.oa(this.submitButton, 'data-oos-popup-trigger')
		if (oosId) {
			const productTitle = q.oa(this.submitButton, 'data-product-title') ?? ''
			openWaitlistModal(parseInt(oosId), productTitle, this.submitButton)
			return
		}
		this.handleErrorMessage()

		this.submitButton.setAttribute('aria-disabled', 'true')
		this.submitButton.setAttribute(ATTRIBUTES.loading, '')
		this.getSpinner().classList.remove('hidden')

		const formData = new FormData(this.form)
		let formVariantId = formData.get('id')
		if (!formVariantId) throw Error('No variant id found')
		const addedVariantId = parseInt(formVariantId.toString())
		const quickAddModal = q.oc<ModalDialog>(
			this,
			'quick-add-modal'
		)

		addItemsToCart(formData, getDOMCartSectionApiIds(), {
			target: this,
			context: quickAddModal ? 'dialog' : 'product',
		})
			.then((cart) => {
				if ('status' in cart) {
					this.handleError(cart, addedVariantId)
					return
				}

				publish(PUB_SUB_EVENTS.cartUpdate, {
					source: 'product-form',
					productVariantId: addedVariantId,
				})
				renderResponseToCartDrawer(cart, quickAddModal)
			})
			.catch((e) => {
				console.error(e)
				publish(PUB_SUB_EVENTS.cartError, {
					source: 'product-form',
					productVariantId: addedVariantId,
					message: window.cartStrings.error,
					errors: window.cartStrings.error,
				})
				showCartErrorNotification()
			})
			.finally(() => {
				this.submitButton.removeAttribute(ATTRIBUTES.loading)

				if (!this.error)
					this.submitButton.removeAttribute('aria-disabled')
				this.getSpinner().classList.add('hidden')
			})
	}

	handleError(cart: CartErrorResponse, addedVariantId: number) {
		publish(PUB_SUB_EVENTS.cartError, {
			source: 'product-form',
			productVariantId: addedVariantId,
			message: cart.message,
			errors: cart.errors ?? cart.description,
		})
		this.handleErrorMessage()
		showCartErrorNotification(cart.description || cart.message)

		const soldOutMessage = this.submitButton.querySelector(
			ProductForm.selectors.soldOutMessage
		)
		if (!soldOutMessage) return
		this.submitButton.setAttribute('aria-disabled', 'true')
		const submitButtonText = q.rs(
			ProductForm.selectors.submitButtonMessage,
			this.submitButton
		)
		submitButtonText.classList.add('hidden')
		soldOutMessage.classList.remove('hidden')
	}

	handleErrorMessage(errorMessage?: string) {
		if (this.hideErrors) return

		this.errorMessageWrapper =
			this.errorMessageWrapper ||
			q.rs(ProductForm.selectors.errorMessageWrapper, this)
		if (!this.errorMessageWrapper)
			throw new Error('No error message wrapper found')
		this.errorMessage =
			this.errorMessage ||
			q.rs(
				ProductForm.selectors.errorMessage,
				this.errorMessageWrapper
			)

		this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage)

		if (errorMessage) {
			this.errorMessage.textContent = errorMessage
		}
	}
}
